import { ApolloClient } from '@apollo/client';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

import { finleyAssert, finleyBuffer } from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';
import { PyCsvVirtualSheetExtractor } from '@finley-technologies/tape-sanitizer';

import { presignedPostUpload } from 'packages/files/ui/utils';
import { GET_PYCVVIRTUALSHEET_UPLOAD_SIGNED_URL } from 'packages/tapeBuilder/requests/tapeBuilder.gql';
import {
  GetExtractPyCSvVirtualSheetQueryGQLType,
  GetExtractPyCSvVirtualSheetDocumentGQLType,
  GetExtractPyCSvVirtualSheetQueryVariablesGQLType,
} from 'packages/tapeBuilder/requests/tapeBuilder.gql.generated';
import { TapeBuilderBuffer } from 'packages/tapeBuilder/ui/calcAgentQA/QAContext';

export const getPyCsvVirtualSheetExtractor = ({
  apolloClient,
  pipelineDefinitionId,
  nodeId,
  getInputNodeIds,
  getNodeById,
  setQAStatus,
  getNodeBuffer,
}: {
  apolloClient: ApolloClient<object>;
  pipelineDefinitionId: string;
  nodeId: string;
  getInputNodeIds: (nodeId: string) => string[];
  getNodeById: (nodeId: string) => TapeBuilderUI.types.TapeBuilderNodeAbstract;
  setQAStatus: (status: string) => Promise<void>;
  getNodeBuffer: (nodeId: string) => TapeBuilderBuffer;
}): PyCsvVirtualSheetExtractor => {
  return async ({ extractionParams }) => {
    const inputNodeIds = getInputNodeIds(nodeId);

    await setQAStatus('Csv extraction: Uploading file.');

    const csvDetails = await Promise.all(
      inputNodeIds.map(async (inputNodeId) => {
        const node = getNodeById(inputNodeId);

        finleyAssert(
          'artifactSlug' in node.configuration &&
            typeof node.configuration.artifactSlug === 'string',
          `artifactSlug must be defined for [nodeId:${inputNodeId}`,
        );

        const buffer = getNodeBuffer(inputNodeId);

        finleyAssert(
          buffer,
          `getPyCSvVirtualSheetExtractor: buffer must exist for [nodeId:${inputNodeId}]`,
        );

        // TypeScript 6.0: Use helper to get a properly typed ArrayBuffer
        const blob = new Blob([finleyBuffer.toArrayBuffer(buffer)], {
          type: 'csv',
        });

        const file = new File(
          [blob],
          `pyCsvVirtualSheetInputFile-${inputNodeId}`,
          {
            type: 'csv',
          },
        );

        // get presignedUrl and upload to s3
        const { queryResponse } = await presignedPostUpload<
          {
            pipelineDefinitionId: string;
            requestId: string;
          },
          { s3Bucket: string; s3Path: string }
        >({
          client: apolloClient,
          file,
          query: GET_PYCVVIRTUALSHEET_UPLOAD_SIGNED_URL,
          queryVariables: {
            pipelineDefinitionId,
            requestId: uuid(),
          },
        });

        return {
          sheetName: node.configuration.artifactSlug,
          s3Bucket: queryResponse.s3Bucket,
          s3Path: queryResponse.s3Path,
        };
      }),
    );

    await setQAStatus('Csv extraction: Starting extraction.');

    // long poll for csv completion signed download url
    // may need to revisit this requests are timing out
    const { data: extractedCsvSignedDownload } = await apolloClient.query<
      GetExtractPyCSvVirtualSheetQueryGQLType,
      GetExtractPyCSvVirtualSheetQueryVariablesGQLType
    >({
      query: GetExtractPyCSvVirtualSheetDocumentGQLType,
      variables: {
        pipelineDefinitionId,
        extractionParams: { csvDetails, ...extractionParams },
      },
    });

    await setQAStatus('Csv extraction: Downloading extracted csv.');

    // download the extract csv to client memory
    const res = await axios({
      url: extractedCsvSignedDownload.extractPyCsvVirtualSheet,
      method: 'GET',
      responseType: 'arraybuffer',
    });

    const buffer = finleyBuffer.fromArrayBuffer(res.data);

    await setQAStatus('');

    return {
      isValid: true,
      data: { outputBuffer: buffer },
      supplementals: [],
    };
  };
};
